/**
 * MakerStack themes — the ~30 two-color palettes a maker can pick in the STYLE
 * tab. Each theme is a flat set of CSS custom-property values consumed by the
 * markup in renderPageBody.ts, so SSR and the editor preview render identically.
 *
 * Each swatch in the picker shows `swatch` (page background) + `accent`.
 */

export interface Theme {
  id: string
  name: string
  /** true if the page background is dark (affects subtle borders/shadows). */
  dark: boolean
  tokens: {
    /** Page background. */
    pageBg: string
    /** Card / surface background. */
    cardBg: string
    /** Primary text. */
    text: string
    /** Secondary / muted text. */
    muted: string
    /** Accent — buttons, links, highlights. */
    accent: string
    /** Text placed on top of the accent color. */
    accentText: string
    /** Hairline borders. */
    border: string
  }
}

const t = (
  id: string,
  name: string,
  dark: boolean,
  pageBg: string,
  cardBg: string,
  text: string,
  muted: string,
  accent: string,
  accentText: string,
  border: string,
): Theme => ({
  id,
  name,
  dark,
  tokens: { pageBg, cardBg, text, muted, accent, accentText, border },
})

export const THEMES: Theme[] = [
  // Light themes
  t("indie", "Indie", false, "#ffffff", "#ffffff", "#0f172a", "#64748b", "#6d28d9", "#ffffff", "#e2e8f0"),
  t("rose", "Rose", false, "#fff1f2", "#ffffff", "#1f1115", "#9f6e78", "#e11d48", "#ffffff", "#fbcfe8"),
  t("emerald", "Emerald", false, "#f0fdf4", "#ffffff", "#08231a", "#4b7a66", "#059669", "#ffffff", "#bbf7d0"),
  t("sky", "Sky", false, "#f0f9ff", "#ffffff", "#0c2233", "#5b7b91", "#0284c7", "#ffffff", "#bae6fd"),
  t("amber", "Amber", false, "#fffbeb", "#ffffff", "#2a1d05", "#8a7340", "#d97706", "#ffffff", "#fde68a"),
  t("violet", "Violet", false, "#faf5ff", "#ffffff", "#1e1033", "#7c6794", "#7c3aed", "#ffffff", "#e9d5ff"),
  t("slate", "Slate", false, "#f8fafc", "#ffffff", "#0f172a", "#64748b", "#334155", "#ffffff", "#e2e8f0"),
  t("coral", "Coral", false, "#fff7ed", "#ffffff", "#2b1308", "#946b54", "#ea580c", "#ffffff", "#fed7aa"),
  t("mint", "Mint", false, "#f0fdfa", "#ffffff", "#062925", "#4a7d75", "#0d9488", "#ffffff", "#99f6e4"),
  t("sand", "Sand", false, "#faf6f0", "#fffdf9", "#33291c", "#8a7a63", "#b45309", "#ffffff", "#ead9c2"),
  t("blush", "Blush", false, "#fdf2f8", "#ffffff", "#2a0f1f", "#9c6b85", "#db2777", "#ffffff", "#fbcfe8"),
  t("ocean", "Ocean", false, "#eff6ff", "#ffffff", "#0c1b3a", "#5a6b8c", "#2563eb", "#ffffff", "#bfdbfe"),
  t("lime", "Lime", false, "#f7fee7", "#ffffff", "#1a2207", "#6b7a45", "#65a30d", "#ffffff", "#d9f99d"),
  t("clean", "Clean", false, "#fafafa", "#ffffff", "#171717", "#737373", "#171717", "#ffffff", "#e5e5e5"),
  t("lavender", "Lavender", false, "#f5f3ff", "#ffffff", "#1f1a33", "#736d8c", "#8b5cf6", "#ffffff", "#ddd6fe"),

  // Dark themes
  t("midnight", "Midnight", true, "#0b1120", "#131c30", "#e2e8f0", "#94a3b8", "#7c5cff", "#ffffff", "#243049"),
  t("carbon", "Carbon", true, "#0a0a0a", "#161616", "#fafafa", "#a3a3a3", "#f5f5f5", "#0a0a0a", "#2a2a2a"),
  t("ink", "Ink", true, "#0f172a", "#1e293b", "#f1f5f9", "#94a3b8", "#38bdf8", "#04293a", "#334155"),
  t("forest", "Forest", true, "#0a1f15", "#102b1e", "#dcfce7", "#86b39c", "#34d399", "#052e1f", "#1c3b2c"),
  t("plum", "Plum", true, "#1a0b2e", "#26143f", "#f3e8ff", "#b09bcb", "#c084fc", "#2a0f45", "#3b2459"),
  t("crimson", "Crimson", true, "#1a0a0e", "#2a1117", "#ffe4e6", "#bf8e96", "#fb7185", "#3a0a14", "#3d1b22"),
  t("steel", "Steel", true, "#111827", "#1f2937", "#f9fafb", "#9ca3af", "#60a5fa", "#0b2545", "#374151"),
  t("amberDark", "Amber Dark", true, "#1c1505", "#2a2008", "#fef3c7", "#bda874", "#fbbf24", "#3a2c05", "#3d3115"),
  t("teal", "Teal", true, "#04201f", "#0a2e2c", "#ccfbf1", "#7eb3ac", "#2dd4bf", "#053330", "#15403c"),
  t("rosewood", "Rosewood", true, "#1f0a12", "#2d1019", "#fce7f0", "#bf8ba2", "#f472b6", "#3a0a1e", "#42182a"),
  t("obsidian", "Obsidian", true, "#000000", "#0f0f0f", "#ededed", "#8f8f8f", "#ffffff", "#000000", "#222222"),
  t("indigo", "Indigo", true, "#0e1033", "#181b4a", "#e0e7ff", "#9aa0d4", "#818cf8", "#10133a", "#2a2f63"),
  t("sunset", "Sunset", true, "#1f0f0a", "#2d160f", "#ffedd5", "#c79a82", "#fb923c", "#3a160a", "#42221a"),
  t("aqua", "Aqua", false, "#ecfeff", "#ffffff", "#083344", "#5b8494", "#06b6d4", "#ffffff", "#a5f3fc"),
  t("gold", "Gold", true, "#14110a", "#211c10", "#fefce8", "#bdb079", "#eab308", "#2a2205", "#332a15"),
]

export const DEFAULT_THEME_ID = "indie"

const THEME_BY_ID: Record<string, Theme> = Object.fromEntries(
  THEMES.map((theme) => [theme.id, theme]),
)

export function getTheme(id: string | undefined): Theme {
  return (id && THEME_BY_ID[id]) || THEME_BY_ID[DEFAULT_THEME_ID]
}

/** Render a theme's tokens as a CSS custom-property block (no selector). */
export function themeCssVars(theme: Theme): string {
  const k = theme.tokens
  return [
    `--ms-page-bg:${k.pageBg}`,
    `--ms-card-bg:${k.cardBg}`,
    `--ms-text:${k.text}`,
    `--ms-muted:${k.muted}`,
    `--ms-accent:${k.accent}`,
    `--ms-accent-text:${k.accentText}`,
    `--ms-border:${k.border}`,
  ].join(";")
}
